import React, { useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { motion } from 'framer-motion';
import { Award } from 'lucide-react';
export function AboutPage() {
  useEffect(() => {
    document.title = "Instructors | Walker's Martial Arts | Bloomington, Illinois";
  }, []);
  const instructors = [{
    name: 'Grandmaster Joe Walker',
    rank: '7th Dan',
    image: 'https://cdn.magicpatterns.com/uploads/nh1Fx2U4VLVqWRnsonycYP/Master_Joe_Walker.png'
  }, {
    name: 'Master Gabriel Walker',
    rank: '6th Dan',
    image: 'https://cdn.magicpatterns.com/uploads/ioM2wh4YMpiqP7G9dUcsrR/Master_Gabriel_Walker.png'
  }, {
    name: 'Master Caitlin Ketterer',
    rank: '5th Dan',
    image: 'https://cdn.magicpatterns.com/uploads/n17WVsbokZtKGqBrvmPFdb/Master_Caitlin_Ketterer.png'
  }];
  return <div className="bg-black min-h-screen" data-id="element-286">
      <Navigation data-id="element-287" />

      {/* Page Header */}
      <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-288">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-289">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-290">
            About <span className="text-[#00D4FF]" data-id="element-291">Walker's</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-292">
            More than just a martial arts school. We are a community dedicated
            to empowering individuals through discipline, respect, and strength.
          </p>
        </motion.div>
      </div>

      {/* Our Story */}
      <section className="py-20 bg-[#111]" data-id="element-293">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-294">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-295">
            <div className="relative" data-id="element-296">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#00D4FF]/20 rounded-full blur-2xl" data-id="element-297" />
              <img src="https://cdn.magicpatterns.com/uploads/p16qSsFXr1o9UMn6JvwfAQ/mayor_brady.jpg" alt="Walker's Martial Arts team with Mayor" className="relative rounded-2xl shadow-2xl z-10" data-id="element-298" />
              <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-[#00D4FF]/30 rounded-2xl z-0" data-id="element-299" />
            </div>

            <div data-id="element-300">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-301">Our Story</h2>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed" data-id="element-302">
                <p data-id="element-303">
                  Founded in 1999, Walker's Martial Arts began with a simple
                  mission: to create a space where anyone, regardless of age or
                  ability, could discover their inner strength.
                </p>
                <p data-id="element-304">
                  Grandmaster Joe Walker, a 7th Degree Black Belt, built this
                  academy on the principles of traditional martial arts while
                  adapting to the needs of modern families. What started as a
                  small studio has grown into Bloomington's premier martial arts
                  facility.
                </p>
                <p data-id="element-305">
                  Today, we are proud to have served thousands of families in
                  our community, helping students build confidence, discipline,
                  and physical fitness that lasts a lifetime in our
                  state-of-the-art 4,200 sq ft facility.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instructors */}
      <section className="py-20 bg-black" data-id="element-306">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-307">
          <h2 className="text-3xl font-bold text-white mb-16 text-center" data-id="element-308">
            Our Instructors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-309">
            {instructors.map((instructor, index) => <motion.div key={instructor.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-[#1a1a1a] rounded-2xl overflow-hidden border border-gray-800 hover:border-[#00D4FF]/50 transition-colors group" data-id="element-310">
                <div className="relative h-64 overflow-hidden" data-id="element-311">
                  <img src={instructor.image} alt={instructor.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-312" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" data-id="element-313" />
                </div>
                <div className="p-6" data-id="element-314">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-315">
                    {instructor.name}
                  </h3>
                  <div className="flex items-center gap-2 text-[#00D4FF]" data-id="element-316">
                    <Award className="w-5 h-5" data-id="element-317" />
                    <span className="font-semibold" data-id="element-318">{instructor.rank}</span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#111]" data-id="element-319">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-320">
          <h2 className="text-3xl font-bold text-white mb-16 text-center" data-id="element-321">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-322">
            {[{
            title: 'Respect',
            desc: 'For ourselves, our instructors, and our community.'
          }, {
            title: 'Discipline',
            desc: 'The bridge between goals and accomplishment.'
          }, {
            title: 'Perseverance',
            desc: 'The strength to keep going when things get tough.'
          }].map((value, index) => <motion.div key={value.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="bg-[#1a1a1a] p-8 rounded-2xl text-center border border-gray-800 hover:border-[#00D4FF] transition-colors" data-id="element-323">
                <h3 className="text-2xl font-bold text-[#00D4FF] mb-4" data-id="element-324">
                  {value.title}
                </h3>
                <p className="text-gray-300" data-id="element-325">{value.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <CTASection data-id="element-326" />
      <Footer data-id="element-327" />
    </div>;
}